/**
 * @TODO Make this a shared package in @travel-frontend namespace
 */
import type {InviaType as IT} from '@/types';

// @TODO expand on this idea, constants, methods for comparing, etc.
export class InviaType {
  inviaType: string;
  mainType: string;
  subType: string;
  supplierType: string;

  constructor(inviaType: string) {
    const [mainType, subType, supplierType] = inviaType.split('#');

    this.inviaType = inviaType;
    this.mainType = mainType ?? '';
    this.subType = subType ?? '';
    this.supplierType = supplierType ?? '';
  }

  toString() {
    return this.inviaType;
  }

  static from(spec: {mainType: string; subType?: string}): InviaType;
  // eslint-disable-next-line @typescript-eslint/unified-signatures -- ...
  static from(spec: {
    mainType: string;
    subType: string;
    supplierType: string;
  }): InviaType;
  static from({
    mainType,
    subType,
    supplierType,
  }: {
    mainType: string;
    subType?: string;
    supplierType?: string;
  }): InviaType {
    return new InviaType(
      [mainType, subType, supplierType].filter(Boolean).join('#')
    );
  }
}

export const isMainType = (inviaType: IT, mainType: string): boolean => {
  return inviaType.startsWith(mainType);
};

export const isBoardType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT06');
};

export const isRoomType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT04');
};

export const isRoomAttributeType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT05');
};

export const isRoomAttributeBalconyTerraceType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT05-BT');
};

export const isRoomAttributeSwimUpPoolType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT05-PO#SUP');
};

export const isRoomViewType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT13');
};

const isHotelAttributeSustainabilityType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-ECO');
};

export const isHotelAttributeFacilityType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-HOT');
};

const isHotelAttributeHotelType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-TYPE');
};

const isHotelAttributeBeachType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-BEA');
};

const isHotelAttributePoolType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-POOL');
};

const isHotelAttributeWellnessType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-WELL');
};

const isHotelAttributeAnimationType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-ANI');
};

const isHotelAttributeChildrenType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-CHI');
};

const isHotelAttributeWaterSportsType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-WSPO');
};

const isHotelAttributeSportsType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-SPO');
};

const isHotelAttributeNearType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-NEA');
};

const isHotelAttributeRestaurantType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT03-REST');
};

export const isHotelTransferType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT14-HTR');
};

export const isRailAndFlyType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT14-RAFL');
};

export const isRentalCarType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT14-RCA');
};

export const isSpecialGroupPromotionType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT08P');
};

export const isSpecialGroupFlexType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT08F');
};

export const isSpecialGroupSafetyType = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT08S');
};

export const isBoardTypeNoBoard = (inviaType: IT): boolean => {
  return inviaType.startsWith('IT06-NB');
};

type HotelAttributesGrouped = {
  hotel: Array<IT>;
  beach: Array<IT>;
  pool: Array<IT>;
  wellness: Array<IT>;
  animation: Array<IT>;
  children: Array<IT>;
  waterSports: Array<IT>;
  sports: Array<IT>;
  near: Array<IT>;
  restaurants: Array<IT>;
  type: Array<IT>;
  sustainability: Array<IT>;
};

export const groupHotelAttributeTypes = (
  hotelAttributes: Array<IT> = []
): HotelAttributesGrouped => {
  return hotelAttributes.reduce<HotelAttributesGrouped>(
    (result, hotelAttribute) => {
      if (isHotelAttributeFacilityType(hotelAttribute)) {
        result.hotel.push(hotelAttribute);
      } else if (isHotelAttributeBeachType(hotelAttribute)) {
        result.beach.push(hotelAttribute);
      } else if (isHotelAttributePoolType(hotelAttribute)) {
        result.pool.push(hotelAttribute);
      } else if (isHotelAttributeWellnessType(hotelAttribute)) {
        result.wellness.push(hotelAttribute);
      } else if (isHotelAttributeAnimationType(hotelAttribute)) {
        result.animation.push(hotelAttribute);
      } else if (isHotelAttributeChildrenType(hotelAttribute)) {
        result.children.push(hotelAttribute);
      } else if (isHotelAttributeWaterSportsType(hotelAttribute)) {
        result.waterSports.push(hotelAttribute);
      } else if (isHotelAttributeSportsType(hotelAttribute)) {
        result.sports.push(hotelAttribute);
      } else if (isHotelAttributeNearType(hotelAttribute)) {
        result.near.push(hotelAttribute);
      } else if (isHotelAttributeRestaurantType(hotelAttribute)) {
        result.restaurants.push(hotelAttribute);
      } else if (isHotelAttributeHotelType(hotelAttribute)) {
        result.type.push(hotelAttribute);
      } else if (isHotelAttributeSustainabilityType(hotelAttribute)) {
        result.type.push(hotelAttribute);
      }
      return result;
    },
    {
      hotel: [],
      beach: [],
      pool: [],
      wellness: [],
      animation: [],
      children: [],
      waterSports: [],
      sports: [],
      near: [],
      restaurants: [],
      type: [],
      sustainability: [],
    }
  );
};
